import type { Draft, PayloadAction } from '@reduxjs/toolkit';

import type { ModelState } from '@proton/account';
import { safeDecreaseCount, safeIncreaseCount } from '@proton/redux-utilities';
import { MAILBOX_LABEL_IDS } from '@proton/shared/lib/constants';
import type { Folder, Label, LabelCount } from '@proton/shared/lib/interfaces';
import type { MessageMetadata } from '@proton/shared/lib/interfaces/mail/Message';

import type { Conversation } from 'proton-mail/models/conversation';

import { getContextNumMessages, getContextNumUnread } from '../../helpers/conversation';
import {
    getCurrentFolderID,
    isCategoryLabel,
    isCustomFolder,
    isCustomLabel,
    isSystemFolder,
    isSystemLabel,
    isUnmodifiableByUser,
} from '../../helpers/location';

export const markConversationsAsUnread = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{ conversations: Conversation[]; labelID: string }>
) => {
    // When we mark a conversation as unread, the latest message of this conversation is marked as unread.
    // This message must be part of the current label (labelID)
    const { conversations, labelID } = action.payload;

    // We assume that it's not possible to mark an unread conversation as unread from the UI.
    // For this reason we don't do any additional checks before updating the conversation counter.
    conversations.forEach((conversation) => {
        const isConversationInInbox = conversation.Labels?.some((label) => label.ID === MAILBOX_LABEL_IDS.INBOX);

        conversation.Labels?.forEach((label) => {
            const conversationCounter = state.value?.find((counter) => counter.LabelID === label.ID);
            if (!conversationCounter) {
                return;
            }

            if (label.ID === labelID) {
                conversationCounter.Unread = safeIncreaseCount(conversationCounter.Unread, 1);
            }

            if (labelID === MAILBOX_LABEL_IDS.INBOX && isConversationInInbox && isCategoryLabel(label.ID)) {
                conversationCounter.Unread = safeIncreaseCount(conversationCounter.Unread, 1);
            }
        });
    });
};

export const markConversationsAsRead = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{ conversations: Conversation[]; labelID: string }>
) => {
    const { conversations } = action.payload;

    conversations.forEach((conversation) => {
        conversation?.Labels?.forEach((conversationLabel) => {
            if (conversationLabel?.ContextNumUnread === 0) {
                return;
            }

            const conversationCounter = state.value?.find((counter) => counter.LabelID === conversationLabel.ID);
            if (conversationCounter) {
                conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);
            }
        });
    });
};

export const markMessagesAsUnread = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{
        messages: MessageMetadata[];
        labelID: string;
        conversations: Conversation[];
        folders: Folder[];
    }>
) => {
    const { conversations, messages, labelID } = action.payload;

    conversations.forEach((conversation) => {
        const hasConversationMessage =
            messages.filter(
                (message) => message.ConversationID === conversation.ID && message.LabelIDs.includes(labelID)
            ).length > 0;

        const conversationLabel = conversation.Labels?.find((label) => label.ID === labelID);

        if (conversationLabel?.ContextNumUnread === 0 && hasConversationMessage) {
            const conversationCounter = state.value?.find((counter) => counter.LabelID === labelID);

            if (conversationCounter) {
                conversationCounter.Unread = safeIncreaseCount(conversationCounter.Unread, 1);
            }
        }

        const convCategoryLabel = conversation.Labels?.find((label) => isCategoryLabel(label.ID));
        if (
            labelID === MAILBOX_LABEL_IDS.INBOX &&
            convCategoryLabel &&
            convCategoryLabel.ContextNumUnread === 0 &&
            hasConversationMessage
        ) {
            const categoryCounter = state.value?.find((counter) => counter.LabelID === convCategoryLabel.ID);

            if (categoryCounter) {
                categoryCounter.Unread = safeIncreaseCount(categoryCounter.Unread, 1);
            }
        }
    });
};

export const markMessagesAsRead = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{
        messages: MessageMetadata[];
        labelID: string;
        conversations: Conversation[];
        folders: Folder[];
        labels: Label[];
    }>
) => {
    const { messages, conversations, folders, labels } = action.payload;

    /* In this action, we are updating messages while being in conversation mode.
     * So we need to update the counters in several places (when needed):
     * - The folder in which the message is (system or custom folder)
     * - All mail
     * - Almost all mail, when messages are not in trash/spam
     * - Starred
     * - Custom labels
     * - Category
     *
     * For that we'll need to check each conversation that are impacted if the number of messages marked as read
     * is marking the conversation as read in one of the location listed above.
     * If so, we can decrease the counter
     */
    conversations.forEach((conversation) => {
        // Get all messages from this conversation that are being marked as read
        const messagesFromConversation = messages.filter((message) => message.ConversationID === conversation.ID);

        if (messagesFromConversation.length === 0) {
            return;
        }

        // Count messages that are not in Spam/Trash to update ALMOST_ALL_MAIL counts
        let nonSpamTrashMessagesCount = 0;

        /**
         * Update message folder counts
         */
        // Group messages by their actual folder
        const messagesByFolder = new Map<string, MessageMetadata[]>();
        messagesFromConversation.forEach((message) => {
            const folderID = getCurrentFolderID(message.LabelIDs, folders);
            if (folderID) {
                const existing = messagesByFolder.get(folderID) || [];
                existing.push(message);
                messagesByFolder.set(folderID, existing);
            }
        });

        messagesByFolder.forEach((folderMessages, folderID) => {
            const conversationLabel = conversation.Labels?.find((label) => label.ID === folderID);
            const messagesCount = folderMessages.length;

            // If all unread messages in this folder are being marked as read, decrease counter
            if (conversationLabel?.ContextNumUnread === messagesCount && messagesCount > 0) {
                const conversationCounter = state.value?.find((counter) => counter.LabelID === folderID);
                if (conversationCounter) {
                    conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);
                }
            }

            // Count messages that are not in Spam/Trash to update ALMOST_ALL_MAIL counts
            if (folderID !== MAILBOX_LABEL_IDS.SPAM && folderID !== MAILBOX_LABEL_IDS.TRASH) {
                nonSpamTrashMessagesCount += messagesCount;
            }
        });

        /**
         * Update ALL_MAIL counts
         */
        const totalMessagesCount = messagesFromConversation.length;
        const conversationAllMailLabel = conversation.Labels?.find((label) => label.ID === MAILBOX_LABEL_IDS.ALL_MAIL);
        if (conversationAllMailLabel?.ContextNumUnread === totalMessagesCount && totalMessagesCount > 0) {
            const allMailCount = state.value?.find((counter) => counter.LabelID === MAILBOX_LABEL_IDS.ALL_MAIL);
            if (allMailCount) {
                allMailCount.Unread = safeDecreaseCount(allMailCount.Unread, 1);
            }
        }

        /**
         * Update ALMOST_ALL_MAIL counts
         * Messages in SPAM & TRASH needs to be ignored
         */
        const conversationAlmostAllMailLabel = conversation.Labels?.find(
            (label) => label.ID === MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL
        );
        if (
            conversationAlmostAllMailLabel?.ContextNumUnread === nonSpamTrashMessagesCount &&
            nonSpamTrashMessagesCount > 0
        ) {
            const almostAllMailCount = state.value?.find(
                (counter) => counter.LabelID === MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL
            );
            if (almostAllMailCount) {
                almostAllMailCount.Unread = safeDecreaseCount(almostAllMailCount.Unread, 1);
            }
        }

        /**
         * Update STARRED counts
         */
        const starredMessagesCount = messagesFromConversation.filter((message) =>
            message.LabelIDs.includes(MAILBOX_LABEL_IDS.STARRED)
        ).length;
        const conversationStarredLabel = conversation.Labels?.find((label) => label.ID === MAILBOX_LABEL_IDS.STARRED);
        // If all starred messages are being marked as read, decrease counter
        if (conversationStarredLabel?.ContextNumUnread === starredMessagesCount && starredMessagesCount > 0) {
            const starredCount = state.value?.find((counter) => counter.LabelID === MAILBOX_LABEL_IDS.STARRED);
            if (starredCount) {
                starredCount.Unread = safeDecreaseCount(starredCount.Unread, 1);
            }
        }

        /**
         * Update custom labels counts
         */
        // Group messages by custom label
        const messagesByCustomLabel = new Map<string, MessageMetadata[]>();
        messagesFromConversation.forEach((message) => {
            message.LabelIDs.forEach((labelID) => {
                if (isCustomLabel(labelID, labels)) {
                    const existing = messagesByCustomLabel.get(labelID) || [];
                    existing.push(message);
                    messagesByCustomLabel.set(labelID, existing);
                }
            });
        });

        messagesByCustomLabel.forEach((labelMessages, labelID) => {
            const conversationLabel = conversation.Labels?.find((label) => label.ID === labelID);
            const messagesCount = labelMessages.length;

            // If all messages in this custom label are being marked as read, decrease counter
            if (conversationLabel?.ContextNumUnread === messagesCount && messagesCount > 0) {
                const labelCounter = state.value?.find((counter) => counter.LabelID === labelID);
                if (labelCounter) {
                    labelCounter.Unread = safeDecreaseCount(labelCounter.Unread, 1);
                }
            }
        });

        /**
         * Update category counts
         * All messages in the conversation share the same category, so we can simply check if we're marking all unread messages as read.
         * Only decrement if at least one of the messages being marked as read is in Inbox (category messages outside Inbox are not counted).
         */
        const hasMessagesInInbox = messagesFromConversation.some(({ LabelIDs }) =>
            LabelIDs.includes(MAILBOX_LABEL_IDS.INBOX)
        );

        const categoryLabels = conversation.Labels?.filter((label) => isCategoryLabel(label.ID)) || [];
        categoryLabels.forEach((categoryLabel) => {
            if (!hasMessagesInInbox) {
                return;
            }
            if (categoryLabel.ContextNumUnread === totalMessagesCount && totalMessagesCount > 0) {
                const categoryCounter = state.value?.find((counter) => counter.LabelID === categoryLabel.ID);
                if (categoryCounter) {
                    categoryCounter.Unread = safeDecreaseCount(categoryCounter.Unread, 1);
                }
            }
        });
    });
};

export const labelMessagesPending = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{
        messages: MessageMetadata[];
        destinationLabelID: string;
        conversations: Conversation[];
        labels: Label[];
        folders: Folder[];
    }>
) => {
    const { messages, conversations, destinationLabelID, labels, folders } = action.payload;

    // When updating item category, decrease old label count and increase new label count
    if (isCategoryLabel(destinationLabelID)) {
        conversations.forEach((conversation) => {
            const sourceCategory = conversation.Labels?.find(({ ID }) => isCategoryLabel(ID))?.ID;

            if (sourceCategory && sourceCategory !== destinationLabelID) {
                const numMessagesInInbox = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.INBOX);
                const numUnreadInInbox = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.INBOX);

                if (numMessagesInInbox === 0) {
                    return;
                }

                const oldCategoryCounter = state.value?.find((counter) => counter.LabelID === sourceCategory);
                const newCategoryCounter = state.value?.find((counter) => counter.LabelID === destinationLabelID);

                // Decrease old category
                if (oldCategoryCounter) {
                    oldCategoryCounter.Total = safeDecreaseCount(oldCategoryCounter.Total, 1);
                    if (numUnreadInInbox > 0) {
                        oldCategoryCounter.Unread = safeDecreaseCount(oldCategoryCounter.Unread, 1);
                    }
                }

                // Increase new category
                if (newCategoryCounter) {
                    newCategoryCounter.Total = safeIncreaseCount(newCategoryCounter.Total, 1);
                    if (numUnreadInInbox > 0) {
                        newCategoryCounter.Unread = safeIncreaseCount(newCategoryCounter.Unread, 1);
                    }
                }
            }
        });
    } else {
        const conversationCounterTarget = state.value?.find((counter) => counter.LabelID === destinationLabelID);

        if (!conversationCounterTarget) {
            return;
        }

        conversations.forEach((conversation) => {
            const messagesFromConversation = messages.filter((message) => message.ConversationID === conversation.ID);
            const unreadMessagesFromConversation = messagesFromConversation.filter((element) => element.Unread);
            const conversationCategory = conversation.Labels?.find(({ ID }) => isCategoryLabel(ID));

            // Decrease
            conversation.Labels?.forEach((label) => {
                const conversationCounter = state.value?.find((counter) => counter.LabelID === label.ID);

                if (!conversationCounter) {
                    return;
                }

                const messagesFromConversationInLabel = messagesFromConversation.filter((element) =>
                    element.LabelIDs.includes(label.ID)
                );
                const unreadMessagesFromConversationInLabel = unreadMessagesFromConversation.filter((element) =>
                    element.LabelIDs.includes(label.ID)
                );

                // Do not decrease counters if moving to a custom label, STARRED, or a category
                if (
                    isCustomLabel(destinationLabelID, labels) ||
                    isSystemLabel(destinationLabelID) ||
                    isUnmodifiableByUser(destinationLabelID, labels, folders)
                ) {
                    return;
                }

                if (destinationLabelID === MAILBOX_LABEL_IDS.TRASH || destinationLabelID === MAILBOX_LABEL_IDS.SPAM) {
                    // Decrease ALMOST_ALL_MAIL and custom labels counts since items are no longer visible there
                    if (label.ID === MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL) {
                        if (messagesFromConversationInLabel.length === getContextNumMessages(conversation, label.ID)) {
                            conversationCounter.Total = safeDecreaseCount(conversationCounter.Total, 1);
                        }

                        if (
                            unreadMessagesFromConversationInLabel.length ===
                                getContextNumUnread(conversation, label.ID) &&
                            unreadMessagesFromConversationInLabel.length > 0
                        ) {
                            conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);
                        }
                        return;
                    }

                    // Do not updated unmodifiable labels. However unread needs to be updated when moving to trash
                    if (isUnmodifiableByUser(label.ID, labels, folders)) {
                        if (
                            destinationLabelID === MAILBOX_LABEL_IDS.TRASH &&
                            unreadMessagesFromConversationInLabel.length ===
                                getContextNumUnread(conversation, label.ID) &&
                            unreadMessagesFromConversationInLabel.length > 0
                        ) {
                            conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);
                        }
                        return;
                    }
                    // Decrease STARRED and custom labels counts since items are removed
                    if (isCustomLabel(label.ID, labels) || isSystemLabel(label.ID)) {
                        if (messagesFromConversationInLabel.length === getContextNumMessages(conversation, label.ID)) {
                            conversationCounter.Total = safeDecreaseCount(conversationCounter.Total, 1);
                        }

                        if (
                            unreadMessagesFromConversationInLabel.length ===
                                getContextNumUnread(conversation, label.ID) &&
                            unreadMessagesFromConversationInLabel.length > 0
                        ) {
                            conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);
                        }
                        return;
                    }
                }

                // Do not update labels, categories and unmodifiable counters
                if (
                    isCustomLabel(label.ID, labels) ||
                    isSystemLabel(label.ID) ||
                    isCategoryLabel(label.ID) ||
                    isUnmodifiableByUser(label.ID, labels, folders)
                ) {
                    return;
                }

                const messageFolderID = messagesFromConversation[0].LabelIDs.find((labelID) => {
                    if (isSystemFolder(labelID) || isCustomFolder(labelID, folders)) {
                        return labelID;
                    }
                });
                // Do nothing if is not the folder in which the message was located
                if (label.ID !== messageFolderID) {
                    return;
                }
                const categoryCounter =
                    conversationCategory && state.value?.find((counter) => counter.LabelID === conversationCategory.ID);

                const shouldUpdateCategoryCounter =
                    conversationCategory && label.ID === MAILBOX_LABEL_IDS.INBOX && categoryCounter;

                // Else decrease count
                if (messagesFromConversationInLabel.length === getContextNumMessages(conversation, label.ID)) {
                    conversationCounter.Total = safeDecreaseCount(conversationCounter.Total, 1);

                    // If we are moving all messages out from INBOX, also reduce category counters
                    if (shouldUpdateCategoryCounter) {
                        categoryCounter.Total = safeDecreaseCount(categoryCounter.Total, 1);
                    }
                }

                if (
                    unreadMessagesFromConversationInLabel.length === getContextNumUnread(conversation, label.ID) &&
                    unreadMessagesFromConversationInLabel.length > 0
                ) {
                    conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);

                    // If we are moving all unread messages out from INBOX, also reduce category counters
                    if (shouldUpdateCategoryCounter) {
                        categoryCounter.Unread = safeDecreaseCount(categoryCounter.Unread, 1);
                    }
                }

                const almostAllMailMessageCountState = state.value?.find(
                    (counter) => counter.LabelID === MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL
                );
                if (
                    (messageFolderID === MAILBOX_LABEL_IDS.TRASH || messageFolderID === MAILBOX_LABEL_IDS.SPAM) &&
                    destinationLabelID !== MAILBOX_LABEL_IDS.TRASH &&
                    destinationLabelID !== MAILBOX_LABEL_IDS.SPAM &&
                    almostAllMailMessageCountState
                ) {
                    if (getContextNumMessages(conversation, MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL) === 0) {
                        almostAllMailMessageCountState.Total = safeIncreaseCount(
                            almostAllMailMessageCountState.Total,
                            1
                        );
                    }

                    if (
                        getContextNumUnread(conversation, MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL) === 0 &&
                        unreadMessagesFromConversationInLabel.length > 0
                    ) {
                        almostAllMailMessageCountState.Unread = safeIncreaseCount(
                            almostAllMailMessageCountState.Unread,
                            1
                        );
                    }
                }
            });

            // Increase
            if (getContextNumMessages(conversation, destinationLabelID) === 0) {
                if (conversationCounterTarget) {
                    conversationCounterTarget.Total = safeIncreaseCount(conversationCounterTarget.Total, 1);
                }
            }

            if (
                getContextNumUnread(conversation, destinationLabelID) === 0 &&
                unreadMessagesFromConversation.length > 0 &&
                destinationLabelID !== MAILBOX_LABEL_IDS.TRASH
            ) {
                if (conversationCounterTarget) {
                    conversationCounterTarget.Unread = safeIncreaseCount(conversationCounterTarget.Unread, 1);
                }
            }

            // When moving to INBOX, also increase category counters if needed
            if (destinationLabelID === MAILBOX_LABEL_IDS.INBOX) {
                const conversationInboxMessages = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.INBOX);
                const conversationInboxUnread = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.INBOX);

                conversation.Labels?.forEach((label) => {
                    if (isCategoryLabel(label.ID)) {
                        const categoryCounter = state.value?.find((c) => c.LabelID === label.ID);

                        if (categoryCounter) {
                            // Increase total if conversation had no messages in inbox
                            if (conversationInboxMessages === 0) {
                                categoryCounter.Total = safeIncreaseCount(categoryCounter.Total, 1);
                            }

                            // Increase unread if conversation had no unread in inbox and we're moving unread messages in
                            if (conversationInboxUnread === 0 && unreadMessagesFromConversation.length > 0) {
                                categoryCounter.Unread = safeIncreaseCount(categoryCounter.Unread, 1);
                            }
                        }
                    }
                });
            }
        });
    }
};

export const labelConversationsPending = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{
        conversations: Conversation[];
        destinationLabelID: string;
        sourceLabelID: string;
        labels: Label[];
        folders: Folder[];
    }>
) => {
    const { conversations, destinationLabelID, labels, folders } = action.payload;

    if (isCategoryLabel(destinationLabelID)) {
        conversations.forEach((conversation) => {
            const sourceCategory = conversation.Labels?.find(({ ID }) => isCategoryLabel(ID))?.ID;

            if (sourceCategory && sourceCategory !== destinationLabelID) {
                const numMessagesInInbox = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.INBOX);
                const numUnreadInInbox = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.INBOX);

                if (numMessagesInInbox === 0) {
                    return;
                }

                const oldCategoryCounter = state.value?.find((counter) => counter.LabelID === sourceCategory);
                const newCategoryCounter = state.value?.find((counter) => counter.LabelID === destinationLabelID);

                // Decrease old category
                if (oldCategoryCounter) {
                    oldCategoryCounter.Total = safeDecreaseCount(oldCategoryCounter.Total, 1);
                    if (numUnreadInInbox > 0) {
                        oldCategoryCounter.Unread = safeDecreaseCount(oldCategoryCounter.Unread, 1);
                    }
                }

                // Increase new category
                if (newCategoryCounter) {
                    newCategoryCounter.Total = safeIncreaseCount(newCategoryCounter.Total, 1);
                    if (numUnreadInInbox > 0) {
                        newCategoryCounter.Unread = safeIncreaseCount(newCategoryCounter.Unread, 1);
                    }
                }
            }
        });
    } else {
        conversations.forEach((conversation) => {
            const numUnreadMessagesInConversation = conversation.NumUnread || 0;

            // DECREASE count in old locations
            conversation.Labels?.forEach((label) => {
                const conversationCountState = state.value?.find((counter) => counter.LabelID === label.ID);

                const hasUnreadInLabel = getContextNumUnread(conversation, label.ID) > 0;

                if (!conversationCountState) {
                    return;
                }

                // If the label cannot be updated, do not update counters
                if (isUnmodifiableByUser(label.ID, labels, folders)) {
                    return;
                }

                if (isSystemLabel(label.ID) || isCustomLabel(label.ID, labels)) {
                    // If moving to TRASH or SPAM, labels are removed
                    if (
                        destinationLabelID === MAILBOX_LABEL_IDS.TRASH ||
                        destinationLabelID === MAILBOX_LABEL_IDS.SPAM
                    ) {
                        conversationCountState.Total = safeDecreaseCount(conversationCountState?.Total, 1);

                        if (hasUnreadInLabel) {
                            conversationCountState.Unread = safeDecreaseCount(conversationCountState?.Unread, 1);
                        }
                    }

                    // Else do not update counters
                    return;
                }

                // Do not update counters when moving to STARRED, custom folders or a category
                if (
                    isCustomLabel(destinationLabelID, labels) ||
                    isSystemLabel(destinationLabelID) ||
                    isCategoryLabel(destinationLabelID)
                ) {
                    return;
                }

                // Remove the conversation messages from all locations (except the destination)
                if (destinationLabelID !== label.ID) {
                    conversationCountState.Total = safeDecreaseCount(conversationCountState?.Total, 1);
                    if (hasUnreadInLabel) {
                        conversationCountState.Unread = safeDecreaseCount(conversationCountState?.Unread, 1);
                    }

                    // If items are moving out from TRASH or SPAM, we need to add them to ALMOST_ALL_MAIL count
                    const almostAllMailCountState = state.value?.find(
                        (counter) => counter.LabelID === MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL
                    );
                    if (
                        (label.ID === MAILBOX_LABEL_IDS.TRASH || label.ID === MAILBOX_LABEL_IDS.SPAM) &&
                        destinationLabelID !== MAILBOX_LABEL_IDS.TRASH &&
                        destinationLabelID !== MAILBOX_LABEL_IDS.SPAM &&
                        almostAllMailCountState
                    ) {
                        if (getContextNumMessages(conversation, MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL) === 0) {
                            almostAllMailCountState.Total = safeIncreaseCount(almostAllMailCountState?.Total, 1);
                        }

                        if (
                            hasUnreadInLabel &&
                            getContextNumUnread(conversation, MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL) === 0
                        ) {
                            almostAllMailCountState.Unread = safeIncreaseCount(
                                almostAllMailCountState?.Unread,
                                getContextNumUnread(conversation, label.ID)
                            );
                        }
                    }

                    return;
                }
            });

            // Elements are removed from ALMOST_ALL_MAIL
            const almostAllMailMessageCountState = state.value?.find(
                (counter) => counter.LabelID === MAILBOX_LABEL_IDS.ALMOST_ALL_MAIL
            );
            if (
                (destinationLabelID === MAILBOX_LABEL_IDS.TRASH || destinationLabelID === MAILBOX_LABEL_IDS.SPAM) &&
                almostAllMailMessageCountState
            ) {
                almostAllMailMessageCountState.Total = safeDecreaseCount(almostAllMailMessageCountState?.Total, 1);

                if (numUnreadMessagesInConversation > 0) {
                    almostAllMailMessageCountState.Unread = safeDecreaseCount(
                        almostAllMailMessageCountState?.Unread,
                        1
                    );
                }
            }

            // Additionally, ALL_MAIL unread count needs to be reduced if some messages were unread
            const allMailMessageCountState = state.value?.find(
                (counter) => counter.LabelID === MAILBOX_LABEL_IDS.ALL_MAIL
            );
            if (
                destinationLabelID === MAILBOX_LABEL_IDS.TRASH &&
                numUnreadMessagesInConversation > 0 &&
                allMailMessageCountState
            ) {
                allMailMessageCountState.Unread = safeDecreaseCount(allMailMessageCountState?.Unread, 1);
            }

            // INCREASE count in destination locations
            const targetMessageCountState = state.value?.find((counter) => counter.LabelID === destinationLabelID);

            if (!targetMessageCountState) {
                return;
            }

            const numMessagesInInbox = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.INBOX);
            const numMessagesInAllSent = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.ALL_SENT);
            const numMessagesInSent = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.SENT);
            const numMessagesInAllDrafts = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.ALL_DRAFTS);
            const numMessagesInDrafts = getContextNumMessages(conversation, MAILBOX_LABEL_IDS.DRAFTS);
            const numMessagesInTarget = getContextNumMessages(conversation, destinationLabelID);
            const numUnreadMessagesInInbox = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.INBOX);
            const numUnreadMessagesInAllSent = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.ALL_SENT);
            const numUnreadMessagesInSent = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.SENT);
            const numUnreadMessagesInAllDrafts = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.ALL_DRAFTS);
            const numUnreadMessagesInDrafts = getContextNumUnread(conversation, MAILBOX_LABEL_IDS.DRAFTS);
            const numUnreadMessagesInTarget = getContextNumUnread(conversation, destinationLabelID);

            const hasUnreadReceivedMessage =
                numUnreadMessagesInConversation - numMessagesInAllDrafts - numMessagesInAllSent > 0;

            if (
                destinationLabelID === MAILBOX_LABEL_IDS.INBOX ||
                destinationLabelID === MAILBOX_LABEL_IDS.SENT ||
                destinationLabelID === MAILBOX_LABEL_IDS.DRAFTS
            ) {
                // Move missing received messages in INBOX
                const inboxMessageCountState = state.value?.find(
                    (counter) => counter.LabelID === MAILBOX_LABEL_IDS.INBOX
                );
                const conversationLabelID = conversation.Labels?.find((label) => isCategoryLabel(label.ID))?.ID;

                if (inboxMessageCountState) {
                    if (numMessagesInInbox === 0) {
                        inboxMessageCountState.Total = safeIncreaseCount(inboxMessageCountState.Total, 1);
                    }

                    if (numUnreadMessagesInInbox === 0 && hasUnreadReceivedMessage) {
                        inboxMessageCountState.Unread = safeIncreaseCount(inboxMessageCountState.Unread, 1);
                    }
                }

                // When conversation enters INBOX, also increase category counters
                if (numMessagesInInbox === 0) {
                    const categoryCountState = state.value?.find((counter) => counter.LabelID === conversationLabelID);

                    if (categoryCountState) {
                        categoryCountState.Total = safeIncreaseCount(categoryCountState.Total, 1);
                    }
                }

                // Conversation had no unread in INBOX before, but has unread received messages (e.g. coming from TRASH/SPAM).
                // Since received messages land in INBOX, the category unread counter should increase.
                if (numUnreadMessagesInInbox === 0 && hasUnreadReceivedMessage) {
                    const categoryCountState = state.value?.find((counter) => counter.LabelID === conversationLabelID);

                    if (categoryCountState) {
                        categoryCountState.Unread = safeIncreaseCount(categoryCountState.Unread, 1);
                    }
                }

                // Move all sent messages to SENT
                const sentMessageCountState = state.value?.find(
                    (counter) => counter.LabelID === MAILBOX_LABEL_IDS.SENT
                );

                if (sentMessageCountState && numMessagesInAllSent > numMessagesInSent) {
                    if (numMessagesInSent === 0) {
                        sentMessageCountState.Total = safeIncreaseCount(sentMessageCountState.Total, 1);
                    }

                    if (numUnreadMessagesInSent === 0 && numUnreadMessagesInAllSent > 0) {
                        sentMessageCountState.Unread = safeIncreaseCount(sentMessageCountState.Unread, 1);
                    }
                }

                // Move all drafts messages to DRAFTS
                const draftsMessageCountState = state.value?.find(
                    (counter) => counter.LabelID === MAILBOX_LABEL_IDS.DRAFTS
                );

                if (draftsMessageCountState && numMessagesInAllDrafts > numMessagesInDrafts) {
                    if (numMessagesInDrafts === 0) {
                        draftsMessageCountState.Total = safeIncreaseCount(draftsMessageCountState.Total, 1);
                    }

                    if (numUnreadMessagesInDrafts === 0 && numUnreadMessagesInAllDrafts > 0) {
                        draftsMessageCountState.Unread = safeIncreaseCount(draftsMessageCountState.Unread, 1);
                    }
                }
            } else {
                if (numMessagesInTarget === 0) {
                    targetMessageCountState.Total = safeIncreaseCount(targetMessageCountState.Total, 1);
                }
                if (
                    numUnreadMessagesInTarget === 0 &&
                    numUnreadMessagesInConversation > 0 &&
                    destinationLabelID !== MAILBOX_LABEL_IDS.TRASH
                ) {
                    targetMessageCountState.Unread = safeIncreaseCount(targetMessageCountState.Unread, 1);
                }
            }
        });
    }
};

export const unlabelConversationsPending = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{ conversations: Conversation[]; destinationLabelID: string; labels: Label[] }>
) => {
    const { conversations, destinationLabelID, labels } = action.payload;
    const conversationCounter = state.value?.find((counter) => counter.LabelID === destinationLabelID);
    const isLabel = isCustomLabel(destinationLabelID, labels) || destinationLabelID === MAILBOX_LABEL_IDS.STARRED;

    if (!isLabel) {
        return;
    }

    if (!conversationCounter) {
        return;
    }

    conversations.forEach((conversation) => {
        conversationCounter.Total = safeDecreaseCount(conversationCounter.Total, 1);

        if (getContextNumUnread(conversation, destinationLabelID) > 0) {
            conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);
        }
    });
};

export const unlabelMessagesPending = (
    state: Draft<ModelState<LabelCount[]>>,
    action: PayloadAction<{
        messages: MessageMetadata[];
        conversations: Conversation[];
        destinationLabelID: string;
        labels: Label[];
    }>
) => {
    const { messages, conversations, destinationLabelID, labels } = action.payload;
    const isLabel = isCustomLabel(destinationLabelID, labels) || destinationLabelID === MAILBOX_LABEL_IDS.STARRED;

    if (!isLabel) {
        return;
    }

    const conversationCounter = state.value?.find((counter) => counter.LabelID === destinationLabelID);

    if (!conversationCounter) {
        return;
    }

    conversations.forEach((conversation) => {
        const messagesFromConversation = messages.filter((message) => message.ConversationID === conversation.ID);
        const unreadMessagesFromConversation = messagesFromConversation.filter((element) => element.Unread);
        const messagesFromConversationInLabel = messagesFromConversation.filter((element) =>
            element.LabelIDs.includes(destinationLabelID)
        );
        const unreadMessagesFromConversationInLabel = unreadMessagesFromConversation.filter((element) =>
            element.LabelIDs.includes(destinationLabelID)
        );
        const conversationLabel = conversation.Labels?.find((label) => label.ID === destinationLabelID);

        if (!conversationLabel) {
            return;
        }

        if (messagesFromConversationInLabel.length === getContextNumMessages(conversation, destinationLabelID)) {
            conversationCounter.Total = safeDecreaseCount(conversationCounter.Total, 1);
        }

        if (unreadMessagesFromConversationInLabel.length === getContextNumUnread(conversation, destinationLabelID)) {
            conversationCounter.Unread = safeDecreaseCount(conversationCounter.Unread, 1);
        }
    });
};
